import React, { useState, useEffect } from 'react';
import { Activity, Database, Cloud, Zap, CheckCircle2, AlertCircle, XCircle, Clock, TrendingUp } from 'lucide-react';

interface ServiceStatus {
  name: string;
  status: 'operational' | 'degraded' | 'outage';
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  lastChecked?: string;
}

interface Incident {
  id: string;
  title: string;
  status: 'resolved' | 'monitoring' | 'investigating';
  description: string;
  timestamp: string;
}

export const StatusPage: React.FC = () => {
  const [services, setServices] = useState<ServiceStatus[]>([
    {
      name: 'FCC ECFS API',
      status: 'operational',
      description: 'Connection to FCC Electronic Comment Filing System',
      icon: Cloud,
      lastChecked: new Date().toLocaleTimeString()
    },
    {
      name: 'AI Services',
      status: 'operational',
      description: 'Document analysis and chat functionality',
      icon: Zap,
      lastChecked: new Date().toLocaleTimeString()
    },
    {
      name: 'Database',
      status: 'operational',
      description: 'User data and document storage',
      icon: Database,
      lastChecked: new Date().toLocaleTimeString()
    },
    {
      name: 'Web Application',
      status: 'operational',
      description: 'Main application interface',
      icon: Activity,
      lastChecked: new Date().toLocaleTimeString()
    }
  ]);

  const [incidents] = useState<Incident[]>([
    // Placeholder for recent incidents
    // {
    //   id: '1',
    //   title: 'Scheduled Maintenance',
    //   status: 'resolved',
    //   description: 'Database optimization completed successfully.',
    //   timestamp: '2024-01-14 02:00 AM EST'
    // }
  ]);

  // Simulate periodic status checks (in production, this would call actual health endpoints)
  useEffect(() => {
    const interval = setInterval(() => {
      setServices(prevServices =>
        prevServices.map(service => ({
          ...service,
          lastChecked: new Date().toLocaleTimeString()
        }))
      );
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'operational':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-700',
          icon: 'text-green-600'
        };
      case 'degraded':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-700',
          icon: 'text-yellow-600'
        };
      case 'outage':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          icon: 'text-red-600'
        };
    }
  };

  const getStatusIcon = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'operational':
        return CheckCircle2;
      case 'degraded':
        return AlertCircle;
      case 'outage':
        return XCircle;
    }
  };

  const getOverallStatus = () => {
    const hasOutage = services.some(s => s.status === 'outage');
    const hasDegraded = services.some(s => s.status === 'degraded');
    
    if (hasOutage) return { status: 'outage', message: 'Some systems are experiencing issues' };
    if (hasDegraded) return { status: 'degraded', message: 'Some systems are running with degraded performance' };
    return { status: 'operational', message: 'All systems operational' };
  };

  const overallStatus = getOverallStatus();
  const overallColors = getStatusColor(overallStatus.status);

  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center space-x-3 mb-6">
          <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-sm border border-green-300/50 shadow-sm">
            <Activity className="w-10 h-10 text-green-700" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-black">
            System Status
          </h1>
        </div>
        <p className="text-gray-700 text-lg max-w-2xl mx-auto">
          Real-time status of CommsGenie services
        </p>
      </div>

      {/* Overall Status Banner */}
      <div className={`${overallColors.bg} border-2 ${overallColors.border} rounded-2xl p-6 sm:p-8 shadow-lg mb-8`}>
        <div className="flex items-center justify-center space-x-4">
          {React.createElement(getStatusIcon(overallStatus.status), {
            className: `w-12 h-12 ${overallColors.icon}`
          })}
          <div className="text-center">
            <h2 className={`text-2xl sm:text-3xl font-bold ${overallColors.text} mb-1`}>
              {overallStatus.status === 'operational' ? 'All Systems Operational' : 
               overallStatus.status === 'degraded' ? 'Degraded Performance' : 
               'Service Disruption'}
            </h2>
            <p className={`text-sm sm:text-base ${overallColors.text}`}>
              {overallStatus.message}
            </p>
          </div>
        </div>
      </div>

      {/* Service Status Grid */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-black mb-6 flex items-center">
          <TrendingUp className="w-6 h-6 text-green-600 mr-2" />
          Service Status
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((service) => {
            const colors = getStatusColor(service.status);
            const StatusIcon = getStatusIcon(service.status);
            const ServiceIcon = service.icon;

            return (
              <div
                key={service.name}
                className={`${colors.bg} border-2 ${colors.border} rounded-xl p-5 shadow-md transition-all duration-300 hover:shadow-lg`}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-white border-2 border-gray-200 flex items-center justify-center">
                      <ServiceIcon className="w-6 h-6 text-gray-700" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-black">{service.name}</h3>
                      <StatusIcon className={`w-5 h-5 ${colors.icon} flex-shrink-0`} />
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{service.description}</p>
                    <div className={`inline-flex items-center space-x-2 text-xs ${colors.text} font-semibold`}>
                      <span className="capitalize">{service.status}</span>
                    </div>
                    {service.lastChecked && (
                      <p className="text-xs text-gray-500 mt-2 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        Last checked: {service.lastChecked}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Incidents */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-black mb-6 flex items-center">
          <Clock className="w-6 h-6 text-green-600 mr-2" />
          Recent Incidents
        </h2>
        {incidents.length > 0 ? (
          <div className="space-y-4">
            {incidents.map((incident) => (
              <div
                key={incident.id}
                className="bg-white border-2 border-gray-200 rounded-xl p-5 shadow-md"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-bold text-black">{incident.title}</h3>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      incident.status === 'resolved'
                        ? 'bg-green-100 text-green-700'
                        : incident.status === 'monitoring'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
                  </span>
                </div>
                <p className="text-gray-700 mb-2">{incident.description}</p>
                <p className="text-sm text-gray-500">{incident.timestamp}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <p className="text-lg font-semibold text-gray-700">No recent incidents</p>
            <p className="text-sm text-gray-600 mt-1">All systems have been running smoothly</p>
          </div>
        )}
      </div>

      {/* Uptime Information */}
      <div className="bg-gradient-to-br from-gray-50 to-green-50 border-2 border-gray-200 rounded-xl p-6 sm:p-8">
        <h2 className="text-xl font-bold text-black mb-4">System Uptime</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">99.9%</p>
            <p className="text-sm text-gray-600 mt-1">Last 30 days</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">99.8%</p>
            <p className="text-sm text-gray-600 mt-1">Last 90 days</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">99.7%</p>
            <p className="text-sm text-gray-600 mt-1">Last 12 months</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-6 text-center">
          Uptime metrics are updated in real-time and reflect the availability of our core services.
        </p>
      </div>

      {/* Subscribe to Updates */}
      <div className="mt-8 text-center">
        <p className="text-gray-600 text-sm">
          Questions about system status? Contact us at{' '}
          <a href="mailto:support@commsgenie.com" className="text-green-600 hover:text-green-800 underline font-semibold">
            support@commsgenie.com
          </a>
        </p>
      </div>
    </div>
  );
};

